import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/library/card";
import { useCourseForm } from "@/features/universityAndCourse/contexts/FormCourseContext";
import { Controller } from "react-hook-form";
import useQualification from "@/module/education/api/useQualification";
import UniversityCombobox from "./UniversityCombobox";
import UniversityMultiCombobox from "./UniversityMultiCombobox";

// Course Type Selection Component
interface CourseTypeProps {
  value?: string;
  onChange: (value: string) => void;
}

function CourseTypeSelector({ value, onChange }: CourseTypeProps) {
  const options = ["Online", "Campus", "Hybrid"];

  return (
    <div className="flex gap-2.5 items-center">
      {options.map((option) => (
        <Button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={`h-7 px-4 rounded-full border transition-all ${
            value === option
              ? "bg-[#335cff] border-[#335cff] text-white hover:bg-[#335cff]"
              : "bg-white border-[#cdd1d0] text-[#374741] hover:bg-white"
          }`}
        >
          <p className="text-sm font-normal tracking-[-0.56px] leading-[1.35]">
            {option}
          </p>
        </Button>
      ))}
    </div>
  );
}

export default function CourseBasics() {
  const { register, errors, control, isEditMode } = useCourseForm();
  const maxOverviewLength = 350;

  const { levelSelects } = useQualification();

  if (!levelSelects) return null;

  return (
    <Card className="bg-white border-[#e6e8e7] w-full gap-4 p-4">
      <CardHeader className="px-0 py-0 gap-0">
        <CardTitle className="font-medium text-base text-black tracking-[-0.32px]">
          Course Basics
        </CardTitle>
      </CardHeader>

      <CardContent className="px-0 py-0">
        <div className="flex flex-col gap-3 pl-2 w-full">
          {/* Course Name */}
          <div className="flex flex-col gap-1.5 w-full">
            <Label
              htmlFor="course-name"
              className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]"
            >
              Course name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="course-name"
              placeholder="Eg. MSc Data Science"
              {...register("name")}
              className={`bg-[#fcfcfc] border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm text-[#505e59] tracking-[-0.56px] ${
                errors.name ? "border-red-500 focus-visible:ring-red-500" : ""
              }`}
            />
            {errors.name?.message && (
              <p className="text-xs text-red-500 tracking-[-0.24px]">
                {String(errors.name.message)}
              </p>
            )}
          </div>

          {/* University — ADD: pick several, the course is created in each.
              EDIT: the one university this row belongs to. */}
          <div className="flex flex-col gap-1.5 w-full">
            <Label
              htmlFor="university"
              className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]"
            >
              {isEditMode ? "University" : "Universities"}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="universityIds"
              control={control}
              render={({ field }) =>
                isEditMode ? (
                  <UniversityCombobox
                    triggerId="university"
                    value={field.value?.[0] ?? undefined}
                    onChange={(id) => field.onChange(id ? [id] : [])}
                    hasError={Boolean(errors.universityIds)}
                  />
                ) : (
                  <UniversityMultiCombobox
                    triggerId="university"
                    values={field.value ?? []}
                    onChange={field.onChange}
                    hasError={Boolean(errors.universityIds)}
                  />
                )
              }
            />
            {errors.universityIds?.message ? (
              <p className="text-xs text-red-500 tracking-[-0.24px]">
                {String(errors.universityIds.message)}
              </p>
            ) : (
              !isEditMode && (
                <p className="text-xs text-[#697571] tracking-[-0.24px]">
                  Pick more than one to add this course to each of them.
                </p>
              )
            )}
          </div>

          {/* Program Type */}
          <div className="flex flex-col gap-1.5 w-full">
            <Label
              htmlFor="program-type"
              className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]"
            >
              Program type <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="level"
              control={control}
              render={({ field }) => (
                <Select
                  key={field.value}
                  value={field.value ?? undefined}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger
                    id="program-type"
                    className={`bg-[#fcfcfc] border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm text-[#505e59] tracking-[-0.56px] w-full ${
                      errors.level
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }`}
                  >
                    <SelectValue placeholder="Select Level" />
                  </SelectTrigger>
                  <SelectContent>
                    {levelSelects.map((val: any, i: number) => (
                      <SelectItem value={val.value} key={i}>
                        {val.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.level?.message && (
              <p className="text-xs text-red-500 tracking-[-0.24px]">
                {String(errors.level.message)}
              </p>
            )}
          </div>

          {/* Course Type */}
          <div className="flex flex-col gap-1.5 w-full">
            <Label className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]">
              Course type <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="courseType"
              control={control}
              render={({ field }) => (
                <CourseTypeSelector
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.courseType?.message && (
              <p className="text-xs text-red-500 tracking-[-0.24px]">
                {String(errors.courseType.message)}
              </p>
            )}
          </div>

          {/* Duration */}
          <div className="flex flex-col gap-1.5 w-full">
            <Label
              htmlFor="duration"
              className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]"
            >
              Duration <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="duration"
                type="number"
                min={1}
                placeholder="Eg. 24"
                {...register("duration")}
                className={`flex-1 bg-[#fcfcfc] border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm text-[#505e59] tracking-[-0.56px] ${
                  errors.duration
                    ? "border-red-500 focus-visible:ring-red-500"
                    : ""
                }`}
              />
              <Controller
                name="durationUnit"
                control={control}
                render={({ field }) => (
                  <Select
                    key={field.value}
                    value={field.value ?? "months"}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger className="w-[120px] bg-[#fcfcfc] border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm text-[#505e59] tracking-[-0.56px]">
                      <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="months">Months</SelectItem>
                      <SelectItem value="years">Years</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {errors.duration?.message && (
              <p className="text-xs text-red-500 tracking-[-0.24px]">
                {String(errors.duration.message)}
              </p>
            )}
          </div>

          {/* Course Overview */}
          <div className="flex flex-col gap-1.5 w-full">
            <Label
              htmlFor="course-overview"
              className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]"
            >
              Course Overview
            </Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <Textarea
                    id="course-overview"
                    placeholder="Write a short summary of the course…"
                    value={field.value || ""}
                    onChange={(e) => {
                      const text = e.target.value;
                      if (text.length <= maxOverviewLength) {
                        field.onChange(text);
                      }
                    }}
                    className={`bg-[#fcfcfc] border-[#cdd1d0] min-h-[100px] rounded-xl px-3 py-2.5 text-sm text-[#505e59] tracking-[-0.56px] resize-none ${
                      errors.description
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }`}
                  />
                  <p className="text-xs text-[#505e59] text-right mt-1 tracking-[-0.24px]">
                    {(field.value || "").length}/{maxOverviewLength}
                  </p>
                </div>
              )}
            />
            {errors.description?.message && (
              <p className="text-xs text-red-500 tracking-[-0.24px]">
                {String(errors.description.message)}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
